import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { 
  Package, 
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  ShoppingCart,
  AlertTriangle,
  TrendingUp,
  Eye
} from 'lucide-react';

interface Product {
  _id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  brand: string;
  supplier: {
    _id: string;
    name: string;
  };
  laboratory?: {
    _id: string;
    name: string;
  };
  costPrice: number;
  salePrice: number;
  stock: number;
  minStock: number;
  maxStock: number;
  unit: string;
  isActive: boolean;
  needsPrescription: boolean;
  isControlled: boolean;
  expirationDate?: string;
  batchNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// CartItem interface is now in CartContext

const Products: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cart = useCart();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);

  // Get supplier filter from URL params
  const supplierFilter = searchParams.get('supplier');
  const supplierName = searchParams.get('supplierName');

  const categories = [
    'Materiales Dentales',
    'Instrumentos',
    'Equipos',
    'Medicamentos',
    'Prótesis',
    'Ortodoncia',
    'Endodoncia',
    'Periodoncia',
    'Cirugía',
    'Radiología',
    'Higiene',
    'Anestesia',
    'Otros'
  ];

  useEffect(() => {
    if (supplierFilter) {
      setSelectedSupplier(supplierFilter);
    }
    loadProducts();
  }, [supplierFilter, selectedCategory, selectedSupplier, showLowStock, searchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedSupplier) params.append('supplier', selectedSupplier);
      if (showLowStock) params.append('needsRestock', 'true');
      params.append('active', 'true');
      params.append('limit', '50');

      const response = await fetch(`/api/products?${params}`);
      if (!response.ok) {
        throw new Error('Error al cargar productos');
      }
      
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  // Cart functions are now available from the cart context

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getStockStatus = (product: Product) => {
    if (product.stock <= 0) return { status: 'out', color: 'red', text: 'Agotado' };
    if (product.stock <= product.minStock) return { status: 'low', color: 'yellow', text: 'Stock bajo' };
    return { status: 'normal', color: 'green', text: 'En stock' };
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSupplier('');
    setShowLowStock(false);
    navigate('/products', { replace: true });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
          {supplierName && (
            <p className="text-sm text-gray-600 mt-1">
              Productos de: <span className="font-medium">{supplierName}</span>
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {cart.getCartItemsCount() > 0 && (
            <button
              onClick={() => cart.toggleCart()}
              className="relative flex items-center gap-2 px-4 py-2 bg-dental-600 text-white rounded-lg hover:bg-dental-700"
            >
              <ShoppingCart className="h-5 w-5" />
              Carrito ({cart.getCartItemsCount()})
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {cart.getCartItemsCount()}
              </span>
            </button>
          )}
          <button className="flex items-center gap-2 px-4 py-2 bg-dental-600 text-white rounded-lg hover:bg-dental-700">
            <Plus className="h-5 w-5" />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Buscar
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Nombre, código o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dental-500 focus:border-dental-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categoría
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-dental-500 focus:border-dental-500"
          >
            <option value="">Todas las categorías</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtros adicionales
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showLowStock}
                onChange={(e) => setShowLowStock(e.target.checked)}
                className="rounded border-gray-300 text-dental-600 focus:ring-dental-500"
              />
              <span className="ml-2 text-sm text-gray-700">Stock bajo</span>
            </label>
          </div>
        </div>

        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Limpiar filtros
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-dental-600"></div>
          <p className="mt-2 text-gray-600">Cargando productos...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map(product => {
            const stockStatus = getStockStatus(product);
            return (
              <div key={product._id} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-sm text-gray-500 mb-1">Código: {product.code}</p>
                    <p className="text-sm text-gray-600">{product.category}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    stockStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                    stockStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {stockStatus.text}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <p><strong>Proveedor:</strong> {product.supplier?.name || 'No especificado'}</p>
                  <p><strong>Stock:</strong> {product.stock} {product.unit}</p>
                  <p><strong>Precio:</strong> {formatPrice(product.salePrice)}</p>
                  {product.brand && <p><strong>Marca:</strong> {product.brand}</p>}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => cart.addToCart(product)}
                    disabled={product.stock <= 0}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-dental-600 text-white rounded-md hover:bg-dental-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Agregar
                  </button>
                  <button className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}

          {products.length === 0 && !loading && (
            <div className="col-span-full text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron productos</h3>
              <p className="text-gray-600">Intenta ajustar los filtros o agregar nuevos productos</p>
            </div>
          )}
        </div>
      )}

      {/* Cart Sidebar */}
      {cart.state.isOpen && cart.state.items.length > 0 && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 border-l">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Carrito de Compras</h2>
              <button
                onClick={() => cart.toggleCart()}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {cart.state.items.map(item => (
                <div key={item.productId} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{item.productName}</h4>
                    <p className="text-xs text-gray-600">{item.productCode}</p>
                    <p className="text-xs text-gray-500">Proveedor: {item.supplier.name}</p>
                    <p className="text-sm font-semibold">{formatPrice(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => cart.updateQuantity(item.productId, item.quantity - 1)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 border rounded hover:bg-gray-50"
                    >
                      -
                    </button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => cart.updateQuantity(item.productId, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 border rounded hover:bg-gray-50"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="font-semibold">Total:</span>
                <span className="font-bold text-lg">{formatPrice(cart.getCartTotal())}</span>
              </div>
              <div className="space-y-2">
                <button 
                  onClick={() => cart.createOrder()}
                  className="w-full py-2 bg-dental-600 text-white rounded-lg hover:bg-dental-700"
                >
                  Realizar Pedido
                </button>
                <button 
                  onClick={() => cart.clearCart()}
                  className="w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Limpiar Carrito
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;